import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@saas-kit/lib';
import { Button, ConfirmDialog, Input } from '@saas-kit/ui';
import { DataTable, type DataTableColumn } from '@saas-kit/ui/DataTable';

import {
  createService,
  deleteService,
  listCategories,
  listServices,
  updateService,
} from './api';
import { ServiceCreateForm, type ServiceCreateInput } from './ServiceCreateForm';
import type { QmsService } from './types';
import { useQmsCrud } from './useQmsCrud';

function ServiceNameArCell({
  value,
  onCommit,
}: {
  value: string | null;
  onCommit: (value: string | null) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(value ?? '');

  const commit = (): void => {
    const next = draft.trim();
    const current = (value ?? '').trim();
    if (next === current) return;
    onCommit(next || null);
  };

  return (
    <Input
      dir="rtl"
      lang="ar"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      className="h-8 w-44 text-end"
    />
  );
}

export function ServicesTab({
  canManage,
  enabled,
}: {
  canManage: boolean;
  enabled: boolean;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const tenantId = useAuth((s) => s.selectedTenant?.tenant_id);
  const crud = useQmsCrud<QmsService, ServiceCreateInput>({
    key: 'qms-services',
    list: listServices,
    create: createService,
    update: updateService,
    remove: deleteService,
    enabled,
  });
  const categoriesQuery = useQuery({
    queryKey: ['qms-categories', tenantId],
    queryFn: listCategories,
    enabled,
  });
  const categories = categoriesQuery.data ?? [];
  const categoryName = (id: string | null) =>
    categories.find((c) => c.id === id)?.name ?? t('fields.none');

  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const columns: DataTableColumn<QmsService>[] = [
    { id: 'code', header: t('fields.code'), cell: (r) => r.code },
    { id: 'name', header: t('fields.name'), cell: (r) => r.name },
    {
      id: 'name_ar',
      header: t('fields.name_ar'),
      cell: (r) =>
        canManage ? (
          <ServiceNameArCell
            value={r.name_ar}
            onCommit={(value) => crud.update(r.id, { name_ar: value })}
          />
        ) : (
          <span dir="rtl" lang="ar">
            {r.name_ar ?? '—'}
          </span>
        ),
    },
    {
      id: 'category',
      header: t('fields.category'),
      cell: (r) => categoryName(r.category_id),
    },
    {
      id: 'daily_cap',
      header: t('fields.daily_cap'),
      align: 'end',
      cell: (r) => r.daily_cap ?? '—',
    },
    {
      id: 'status',
      header: t('fields.status'),
      cell: (r) => (r.is_active ? t('status.active') : t('status.inactive')),
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      exportSkip: true,
      cell: (r) =>
        canManage ? (
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => crud.update(r.id, { is_active: !r.is_active })}
            >
              {r.is_active ? t('actions.deactivate') : t('actions.activate')}
            </Button>
            <Button variant="ghost" onClick={() => setPendingDelete(r.id)}>
              {t('actions.delete')}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      {canManage ? (
        <ServiceCreateForm
          categories={categories}
          creating={crud.creating}
          onCreate={(input) => crud.create(input)}
        />
      ) : null}

      <DataTable
        data={crud.items}
        columns={columns}
        rowKey={(r) => r.id}
        selectable={canManage}
        isLoading={crud.isLoading}
        error={crud.isError ? t('errors.view_denied') : null}
        emptyText={t('empty')}
        bulkActions={
          canManage
            ? [
                {
                  id: 'delete',
                  label: t('actions.delete'),
                  variant: 'destructive',
                  onRun: (rows) => crud.removeMany(rows.map((r) => r.id)),
                },
              ]
            : []
        }
      />

      {pendingDelete ? (
        <ConfirmDialog
          title={t('confirm.delete_title')}
          body={t('confirm.delete_body')}
          confirmLabel={t('confirm.confirm')}
          cancelLabel={t('confirm.cancel')}
          variant="destructive"
          isBusy={crud.removing}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            crud.remove(pendingDelete);
            setPendingDelete(null);
          }}
        />
      ) : null}
    </div>
  );
}
